import * as React from "react";
import * as SwitchPrimitive from "@radix-ui/react-switch";

import { cn } from "@/lib/utils";

/**
 * Binary on/off control. Reach for this over `Checkbox` when the
 * vocabulary is "is enabled / is disabled" — feature flags, settings
 * toggles, "show archived" filters — not "select one of several."
 *
 * Token-driven: `bg-primary-fill` when on, `bg-switch-track-off` +
 * `border-border` when off; `primary-mid` focus ring; opacity dim on
 * `disabled`. The thumb flips `bg-white` → `bg-primary-foreground` between
 * off/on so it contrasts its track in every theme palette. Matches the
 * `Checkbox` primitive's chrome so the two read as the same family
 * (different shape, same brand).
 *
 * Per CLAUDE.md: any primitive used as a child of `*Trigger asChild`
 * must `React.forwardRef`. Radix's `Root` already forwards refs, so
 * this wrapper preserves that contract.
 */
export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      // Track. 36×20 with a 2px inner padding so the thumb has room
      // to travel without clipping.
      "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-mid focus-visible:ring-offset-1 focus-visible:ring-offset-bg",
      "disabled:cursor-not-allowed disabled:opacity-50",
      // Off state: a dedicated `switch-track-off` fill (not `surface-elevated`)
      // that is defined per-palette to always sit a clear step from the card
      // surface — so the white knob reads in every theme, including the mono
      // palettes where all the neutral surfaces cluster in a narrow band and
      // a white-on-near-white knob would otherwise vanish.
      "data-[state=unchecked]:border-border data-[state=unchecked]:bg-switch-track-off",
      // On state: branded teal, both border + fill so the contrast
      // works in light + dark themes (border-color swap alone isn't
      // enough in light mode).
      "data-[state=checked]:border-primary-fill data-[state=checked]:bg-primary-fill",
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb
      className={cn(
        // Thumb. 16px disc with shadow so it lifts off the track in
        // either state. Travels 16px from left to right.
        "pointer-events-none block h-4 w-4 rounded-full bg-white shadow-card ring-0 transition-transform",
        // Checked thumb flips to `primary-foreground` (same as the Checkbox's
        // check mark) so the disc contrasts the `primary-fill` track in EVERY
        // palette — including dark-mono, where the fill is near-white and a
        // plain white thumb would vanish. The off-state thumb stays white; the
        // `switch-track-off` fill is what guarantees it reads in every theme.
        "data-[state=checked]:bg-primary-foreground",
        "data-[state=unchecked]:translate-x-0.5 data-[state=checked]:translate-x-[18px]",
      )}
    />
  </SwitchPrimitive.Root>
));
Switch.displayName = SwitchPrimitive.Root.displayName;
